import posthog from "posthog-js";

import env from "@/lib/env-public";

export const initPostHog = () => {
  // Debug logging to help troubleshoot production issues
  // console.log("PostHog Debug Info:", {
  //   hasWindow: typeof window !== "undefined",
  //   hasKey: !!env.NEXT_PUBLIC_POSTHOG_KEY,
  //   keyExists: env.NEXT_PUBLIC_POSTHOG_KEY ? "SET" : "MISSING",
  //   keyPrefix: env.NEXT_PUBLIC_POSTHOG_KEY ? env.NEXT_PUBLIC_POSTHOG_KEY.substring(0, 10) + "..." : "NONE",
  //   host: env.NEXT_PUBLIC_POSTHOG_HOST || "using default (https://us.posthog.com)",
  //   hostname: typeof window !== "undefined" ? window.location.hostname : "SSR",
  //   nodeEnv: process.env.NODE_ENV,
  // });

  if (typeof window !== "undefined" && env.NEXT_PUBLIC_POSTHOG_KEY) {
    // console.log("PostHog: Initializing with key:", env.NEXT_PUBLIC_POSTHOG_KEY.substring(0, 10) + "...");

    posthog.init(env.NEXT_PUBLIC_POSTHOG_KEY, {
      api_host: env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.posthog.com",
      person_profiles: "identified_only",
      capture_pageview: false, // We'll handle pageviews manually for better control
      capture_pageleave: true, // Automatically capture when users leave pages
      session_recording: {
        recordCrossOriginIframes: true,
      },
      autocapture: true,
      disable_session_recording: false,
      // Capture unhandled errors / rejections so frontend crashes (e.g. render-loop
      // "Maximum update depth exceeded") surface in PostHog Error Tracking and can alert,
      // instead of only being visible via customer reports.
      capture_exceptions: true,

      // Add loaded callback for more debugging
      loaded: posthog => {
        console.log("PostHog: Successfully loaded and initialized", {
          config: posthog.config,
          isLoaded: posthog.__loaded,
        });
      },
    });

    console.log("PostHog: Init function called successfully");
  } else {
    console.warn("PostHog: Not initialized. Reason:", {
      hasWindow: typeof window !== "undefined",
      hasKey: !!env.NEXT_PUBLIC_POSTHOG_KEY,
      issue: !env.NEXT_PUBLIC_POSTHOG_KEY
        ? "Missing NEXT_PUBLIC_POSTHOG_KEY"
        : "Not in browser environment",
    });
  }
};

export const identifyUser = (userId: string, properties?: Record<string, any>) => {
  if (typeof window !== "undefined") {
    posthog.identify(userId, properties);
  }
};

export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  if (typeof window !== "undefined") {
    console.log("PostHog: Tracking event", {
      eventName,
      properties,
      isPostHogLoaded: typeof posthog !== "undefined" && posthog.__loaded,
    });
    posthog.capture(eventName, properties);
  } else {
    console.warn("PostHog: Cannot track event (not in browser)", { eventName });
  }
};

export const trackPageView = (url: string, properties?: Record<string, any>) => {
  if (typeof window !== "undefined") {
    console.log("PostHog: Tracking pageview", {
      url,
      properties,
      isPostHogLoaded: typeof posthog !== "undefined" && posthog.__loaded,
    });
    posthog.capture("$pageview", {
      $current_url: url,
      ...properties,
    });
  } else {
    console.warn("PostHog: Cannot track pageview (not in browser)", { url });
  }
};

const normalizePageName = (url: string): string => {
  // Extract pathname from full URL if needed
  let pathPart: string;
  let queryPart: string | undefined;
  try {
    // Try to parse as a full URL first
    const urlObj = new URL(url);
    pathPart = urlObj.pathname;
    queryPart = urlObj.search.slice(1); // Remove the '?' prefix
  } catch {
    // If URL parsing fails, treat as relative path
    [pathPart, queryPart] = url.split("?");
    pathPart = pathPart.split("#")[0]; // Remove hash
  }

  // Define important query parameters to preserve
  const importantParams = ["t"]; // Add more as needed

  // Extract important query parameters
  const preservedParams: string[] = [];
  if (queryPart) {
    const urlParams = new URLSearchParams(queryPart);
    importantParams.forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        preservedParams.push(`${param}=${value}`);
      }
    });
  }

  // Define patterns to normalize the path
  const patterns = [
    // Dashboard hub with UUID -> /dashboard/hub
    { pattern: /^\/dashboard\/hub\/[a-f0-9-]{36}/, replacement: "/dashboard/hub" },
  ];

  // Apply patterns to the path
  let normalizedPath = pathPart;
  for (const { pattern, replacement } of patterns) {
    if (pattern.test(pathPart)) {
      normalizedPath = replacement;
      break;
    }
  }

  // Reconstruct URL with preserved query parameters
  if (preservedParams.length > 0) {
    return `${normalizedPath}?${preservedParams.join("&")}`;
  }

  return normalizedPath;
};

export const startPageTimer = (pageName: string) => {
  if (typeof window !== "undefined") {
    const normalizedPageName = normalizePageName(pageName);
    const startTime = Date.now();
    sessionStorage.setItem(`page_start_${normalizedPageName}`, startTime.toString());
  }
};

export const endPageTimer = (pageName: string, properties?: Record<string, any>) => {
  if (typeof window !== "undefined") {
    const normalizedPageName = normalizePageName(pageName);
    const startTimeStr = sessionStorage.getItem(`page_start_${normalizedPageName}`);
    if (startTimeStr) {
      const startTime = parseInt(startTimeStr);
      const endTime = Date.now();
      const timeSpent = Math.round((endTime - startTime) / 1000); // Convert to seconds

      trackEvent("page_time_spent", {
        page: normalizedPageName,
        page_original: pageName, // Keep original URL for reference
        time_spent_seconds: timeSpent,
        time_spent_minutes: Math.round((timeSpent / 60) * 100) / 100, // Round to 2 decimal places
        ...properties,
      });

      // Clean up
      sessionStorage.removeItem(`page_start_${normalizedPageName}`);
    }
  }
};

export const captureException = (error: Error, properties?: Record<string, unknown>) => {
  if (typeof window !== "undefined") {
    posthog.captureException(error, properties);
  }
};

export const setUserProperties = (properties: Record<string, any>) => {
  if (typeof window !== "undefined") {
    posthog.people.set(properties);
  }
};
